import { useMemo } from "react";
import type { MatrixClient } from "matrix-js-sdk";
import { Avatar, Text } from "../../theme/primitives";
import { avatarHttpUrl, type ConversationSummary } from "../../matrix/rooms";
import { formatConversationTime } from "../../lib/time";

interface ConversationRowProps {
  client: MatrixClient;
  conversation: ConversationSummary;
  selected: boolean;
  onSelect: (roomId: string) => void;
}

export function ConversationRow({ client, conversation, selected, onSelect }: ConversationRowProps) {
  const avatarUrl = useMemo(
    () => avatarHttpUrl(client, conversation.other?.avatarMxc ?? null),
    [client, conversation.other?.avatarMxc],
  );

  return (
    <button
      onClick={() => onSelect(conversation.roomId)}
      style={{
        display: "flex",
        alignItems: "center",
        gap: "var(--space-md)",
        width: "100%",
        border: "none",
        borderBottom: "1px solid var(--color-border)",
        background: selected ? "var(--color-bg-subtle)" : "var(--color-panel)",
        padding: "var(--space-md) var(--space-lg)",
        textAlign: "left",
      }}
    >
      <Avatar name={conversation.title} src={avatarUrl} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-sm)" }}>
          <Text weight="medium" truncate>
            {conversation.title}
          </Text>
          <Text muted size="sm" style={{ flexShrink: 0 }}>
            {conversation.lastActivityTs
              ? formatConversationTime(conversation.lastActivityTs)
              : ""}
          </Text>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", gap: "var(--space-sm)", marginTop: 2 }}>
          <Text muted size="sm" truncate style={{ flex: 1 }}>
            {conversation.lastMessage || "No messages yet"}
          </Text>
          {conversation.unreadCount > 0 && (
            <span
              style={{
                minWidth: 20,
                height: 20,
                padding: "0 6px",
                borderRadius: "var(--radius-pill)",
                background: "var(--color-unread)",
                color: "var(--color-accent-contrast)",
                fontSize: "var(--font-size-sm)",
                fontWeight: 600,
                display: "inline-flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {conversation.unreadCount}
            </span>
          )}
        </div>
      </div>
    </button>
  );
}
